/**
 * The Swatch Book (sections/lig-swatch-book.liquid).
 *
 * Filters: place pills and colour dots, combined (a card shows when it matches
 * both). The photo tiles only show on the unfiltered grid.
 *
 * Quick shop: a <dialog> filled from the section's JSON (the pajamas' and the
 * paper's real variants, prices, availability, photos and product-page
 * descriptions). It carries everything a shopper needs before buying: every
 * product photo (tap to enlarge in the photo viewer), the fabric-and-fit text,
 * the theme's own size guide and delivery window (rendered hidden by
 * sections/lig-swatch-book-extras.liquid and moved in here once), the paper's
 * photos and specs, and plain links to both product pages.
 *
 * Who's wearing it (2026-10-02): the drawer keeps a list of people, one row
 * each — a grown-up (the pajamas) or, when the print has kids' lounge pants, a
 * kid — each with their own size. One person is the old single-size picker.
 * Add posts every pair and, if chosen, a roll of the matching paper in ONE
 * /cart/add.js call, then dispatches CartAddEvent so the header count and drawer
 * update — the same path as lig-card-maker.js / lig-ornament-maker.js. Kids'
 * pieces are called lounge pants, never pajamas, and the drawer says they
 * arrive in their own box.
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

// Shopify image URLs already carry ?width=…; swap it rather than adding a second one.
const sized = (src, w) => (/[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, `$1width=${w}`) : `${src}${src.includes('?') ? '&' : '?'}width=${w}`);

const money = (cents) => {
  const n = cents / 100;
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

// "Holiday Harbor Wide-Leg Pajama Pants — close-up of the print" → "Print"
const LABELS = [[/close-up/i, 'Print'], [/at home/i, 'At home'], [/women/i, "Women's"], [/men/i, "Men's"], [/flat/i, 'Flat'], [/back/i, 'Back'],
  [/unrolled/i, 'Unrolled'], [/scissors/i, 'The roll'], [/rolled/i, 'Rolled'], [/square/i, 'The print'], [/15 ft/i, '15 ft roll'], [/6 ft/i, '6 ft roll']];
const label = (alt) => {
  for (const [re, name] of LABELS) if (re.test(alt || '')) return name;
  return 'Photo';
};
const caption = (alt) => (alt || '').split(' — ').slice(1).join(' — ') || alt || '';

class SwatchBook {
  constructor(root) {
    this.root = root;
    this.data = {};
    try { this.data = JSON.parse(root.querySelector('[data-lsb-prints]')?.textContent || '{}'); } catch { this.data = {}; }
    this.grid = root.querySelector('.lsb-grid');
    this.cards = [...root.querySelectorAll('.lsb-card')];
    this.edits = [...root.querySelectorAll('.lsb-edit')];
    this.count = root.querySelector('.lsb-count');
    this.empty = root.querySelector('.lsb-empty');
    this.filterBar = root.querySelector('.lsb-filter');
    this.place = 'all';
    this.colour = null;
    this.dialog = root.querySelector('.lsb-qs');
    this.zoom = root.querySelector('.lsb-zoom');
    this.current = null;
    this.people = [];
    this.busy = false;
    this.photos = [];
    this.photoIndex = 0;
    this.bindFilters();
    this.bindQuickShop();
    this.bindZoom();
  }

  // ---- filters ----------------------------------------------------------------
  bindFilters() {
    this.root.querySelector('.lsb-pills')?.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-f]');
      if (!b) return;
      this.place = b.dataset.f;
      this.root.querySelectorAll('.lsb-pills button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      this.apply();
    });
    this.root.querySelector('.lsb-dots')?.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-c]');
      if (!b) return;
      this.colour = this.colour === b.dataset.c ? null : b.dataset.c;
      this.root.querySelectorAll('.lsb-dots button').forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.c === this.colour)));
      this.apply();
    });
    this.empty?.addEventListener('click', (e) => {
      if (!e.target.closest('[data-clear]')) return;
      this.colour = null;
      this.root.querySelectorAll('.lsb-dots button').forEach((x) => x.setAttribute('aria-pressed', 'false'));
      this.apply();
    });
  }

  apply() {
    let n = 0;
    this.cards.forEach((c) => {
      const on = (this.place === 'all' || c.dataset.place === this.place) && (!this.colour || c.dataset.colour === this.colour);
      c.hidden = !on;
      if (on) n += 1;
    });
    const unfiltered = this.place === 'all' && !this.colour;
    this.edits.forEach((t) => { t.hidden = !unfiltered; });
    if (this.empty) this.empty.hidden = n > 0;
    if (this.count) this.count.textContent = `${n} print${n === 1 ? '' : 's'}`;
    // keep the results in view: if the grid's top has scrolled above the bar, bring it back
    const barBottom = this.filterBar ? this.filterBar.getBoundingClientRect().bottom : 0;
    const top = this.grid.getBoundingClientRect().top;
    if (top < barBottom - 1) {
      window.scrollTo({ top: window.scrollY + top - barBottom, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  // ---- quick shop -------------------------------------------------------------
  bindQuickShop() {
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return; // no <dialog>: covers stay plain product links
    const q = (s) => this.dialog.querySelector(s);
    this.el = {
      main: q('.lsb-qs__main'), stage: q('.lsb-qs__stage'), thumbs: q('.lsb-qs__thumbs'),
      eyebrow: q('.lsb-qs__eyebrow'), title: q('.lsb-qs__title'), line: q('.lsb-qs__line'), pdp: q('.lsb-qs__pdp'),
      people: q('.lsb-qs__people'), addPerson: q('[data-add-person]'), kidsNote: q('.lsb-qs__kidsnote'),
      sizeLabel: q('.lsb-qs__sizes .lsb-qs__legend > span'), kidsGuide: q('[data-slot="sizeguide-kids"]'),
      paperGroup: q('.lsb-qs__paper'), rolls: q('.lsb-qs__rolls'),
      add: q('.lsb-qs__add'), status: q('.lsb-qs__status'), after: q('.lsb-qs__after'),
      pjText: q('[data-rte="pajamas"]'), paperFold: q('[data-fold="paper"]'), paperText: q('[data-rte="paper"]'),
      paperPhotos: q('.lsb-qs__paperphotos'), paperLink: q('.lsb-qs__paperlink'),
      dogFold: q('[data-fold="dog"]'), dogText: q('[data-rte="dog"]'), dogLink: q('[data-dog-link]'),
      kidsFold: q('[data-fold="kids"]'), kidsText: q('[data-rte="kids"]'), kidsLink: q('[data-kids-link]'),
    };
    this.adoptExtras();
    this.grid.addEventListener('click', (e) => {
      const a = e.target.closest('[data-quick]');
      if (!a || !this.data[a.dataset.quick]) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // let a new-tab click open the product page
      e.preventDefault();
      this.open(a.dataset.quick);
    });
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog || e.target.closest('[data-close]')) { this.dialog.close(); return; }
      if (e.target.closest('[data-zoom]')) { this.openZoom(this.photos, this.photoIndex); return; }
      const t = e.target.closest('.lsb-qs__thumbs button');
      if (t) { this.show(Number(t.dataset.i)); return; }
      const pp = e.target.closest('.lsb-qs__paperphotos button');
      if (pp) { this.openZoom(this.current.paper.gallery, Number(pp.dataset.i)); return; }
      if (e.target.closest('[data-open-paper]')) {
        this.el.paperFold.open = true;
        this.el.paperFold.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const s = e.target.closest('.lsb-qs__chips button');
      if (s && !s.disabled) { this.pick(Number(s.closest('[data-person]').dataset.person), s.dataset.id); return; }
      const w = e.target.closest('[data-who]');
      if (w) { this.setWho(Number(w.closest('[data-person]').dataset.person), w.dataset.who); return; }
      const x = e.target.closest('[data-remove]');
      if (x) { this.removePerson(Number(x.closest('[data-person]').dataset.person)); return; }
      if (e.target.closest('[data-add-person]')) { this.addPerson(); return; }
      if (e.target.closest('.lsb-qs__add')) { this.addToCart(); return; }
      if (e.target.closest('[data-again]')) {
        // a fresh start: nobody chosen and no second roll of paper by accident
        const none = this.dialog.querySelector('input[name="lsb-paper"][value=""]');
        if (none) none.checked = true;
        this.resetPeople(); this.el.after.hidden = true; this.status('');
        this.el.people.querySelector('.lsb-qs__chips button:not(:disabled)')?.focus();
        return;
      }
      if (e.target.closest('[data-cart]')) {
        this.dialog.close();
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') { try { drawer.open(); return; } catch { /* full-page cart */ } }
        window.location.href = (window.Theme?.routes?.cart_url) || '/cart';
      }
    });
    this.dialog.addEventListener('change', (e) => { if (e.target.name === 'lsb-paper') this.total(); });
  }

  /** Move the theme's own size guide and delivery window (rendered hidden by
   *  the extras section) into the drawer. Moving keeps the size guide's own
   *  click handlers; nothing is copied. */
  adoptExtras() {
    const extras = document.querySelector('[data-lsb-extras]');
    if (!extras) return;
    const guide = extras.querySelector('[data-extra="sizeguide"] .lig-sizeguide');
    const kidsGuide = extras.querySelector('[data-extra="sizeguide-kids"] .lig-sizeguide');
    const timeline = extras.querySelector('[data-extra="timeline"] .ship-timeline');
    if (guide) this.dialog.querySelector('[data-slot="sizeguide"]').append(guide);
    if (kidsGuide) {
      // the same size-guide block, for the kids' pants: say whose sizes it shows
      const open = kidsGuide.querySelector('.lig-sizeguide__open');
      const text = open && [...open.childNodes].reverse().find((n) => n.nodeType === 3 && n.textContent.trim());
      if (text) text.textContent = " Kids' sizes";
      this.el.kidsGuide.append(kidsGuide);
    }
    if (timeline) this.dialog.querySelector('[data-slot="timeline"]').append(timeline);
  }

  thumb(src, alt, i, pressed) {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset.i = String(i);
    if (pressed !== undefined) b.setAttribute('aria-pressed', String(pressed));
    b.setAttribute('aria-label', caption(alt) || label(alt));
    const img = document.createElement('img');
    img.src = sized(src, 200); img.alt = ''; img.width = 140; img.height = 140; img.loading = 'lazy';
    const tag = document.createElement('span');
    tag.textContent = label(alt);
    b.append(img, tag);
    return b;
  }

  open(id) {
    const d = this.data[id];
    this.current = d;
    // photos: every pajama photo, the kids' pair (not its close-up — the
    // grown-ups' already shows the print), then the paper's print
    this.photos = [...(d.pajamas.gallery || [])];
    if (d.kids) {
      for (const p of (d.kids.gallery || []).filter((g) => !/close-up/i.test(g.alt)).slice(0, 3)) this.photos.push({ ...p, kids: true });
    }
    if (d.dog) {
      for (const p of (d.dog.gallery || []).slice(0, 3)) this.photos.push({ ...p, dog: true });
    }
    if (d.paper && d.paper.gallery?.length) {
      const sq = d.paper.gallery.find((p) => /square/i.test(p.alt)) || d.paper.gallery[0];
      this.photos.push({ src: sq.src, alt: `${d.paper.title} — the print, a 24 in square`, paper: true });
    }
    this.el.thumbs.replaceChildren(...this.photos.map((p, i) => {
      const b = this.thumb(p.src, p.alt, i, i === 0);
      if (p.paper) b.querySelector('span').textContent = 'Paper';
      if (p.kids) b.querySelector('span').textContent = 'Kids';
      if (p.dog) b.querySelector('span').textContent = 'Dog';
      return b;
    }));
    this.show(0);
    this.el.eyebrow.textContent = [`No. ${d.no}`, d.place].filter(Boolean).join(' · ');
    this.el.title.textContent = d.name;
    this.el.line.textContent = d.line || '';
    this.el.line.hidden = !d.line;
    this.el.pdp.href = d.pajamas.url;
    this.el.pjText.innerHTML = d.pajamas.details || '';
    // who's wearing it: one grown-up to start
    this.el.sizeLabel.textContent = (d.kids || d.dog) ? "Who's wearing it?" : 'Pajama size';
    this.el.kidsGuide.hidden = !d.kids || !this.el.kidsGuide.firstElementChild;
    this.el.kidsFold.hidden = !d.kids;
    if (d.kids) {
      this.el.kidsText.innerHTML = d.kids.details || '';
      this.el.kidsLink.href = d.kids.url;
      this.el.kidsFold.open = false;
    }
    this.el.dogFold.hidden = !d.dog;
    if (d.dog) {
      this.el.dogText.innerHTML = d.dog.details || '';
      this.el.dogLink.href = d.dog.url;
      this.el.dogFold.open = false;
    }
    this.resetPeople();
    // paper
    const paper = d.paper && d.paper.variants.some((v) => v.available) ? d.paper : null;
    this.el.paperGroup.hidden = !paper;
    this.el.paperFold.hidden = !paper;
    if (paper) {
      const opts = [{ id: '', text: 'No paper' },
        ...paper.variants.filter((v) => v.available).map((v) => ({ id: String(v.id), text: `${v.title} · ${money(v.price)}`, note: `30 in × ${v.title.replace(/\s*roll$/i, '')}` }))];
      this.el.rolls.replaceChildren(...opts.map((o, i) => {
        const l = document.createElement('label');
        const r = document.createElement('input');
        r.type = 'radio'; r.name = 'lsb-paper'; r.value = o.id; r.checked = i === 0;
        l.append(r, document.createTextNode(` ${o.text}`));
        if (o.note) { const sm = document.createElement('small'); sm.textContent = o.note; l.append(sm); }
        return l;
      }));
      this.el.paperText.innerHTML = paper.details || '';
      this.el.paperPhotos.replaceChildren(...(paper.gallery || []).map((p, i) => this.thumb(p.src, p.alt, i)));
      this.el.paperLink.href = paper.url;
      this.el.paperFold.open = false;
    }
    this.el.after.hidden = true;
    this.status('');
    this.total();
    if (!this.dialog.open) this.dialog.showModal();
    this.dialog.querySelector('.lsb-qs__panel').scrollTop = 0;
    this.el.people.querySelector('.lsb-qs__chips button:not(:disabled)')?.focus({ preventScroll: true });
  }

  // ---- who's wearing it ---------------------------------------------------------
  /** The variants a person picks from: the pajamas, or the kids' lounge pants. */
  variantsFor(p) {
    return (p.who === 'dog' ? this.current.dog?.variants : p.who === 'kid' ? this.current.kids?.variants : this.current.pajamas.variants) || [];
  }

  resetPeople() {
    this.people = [{ who: 'adult', id: null }];
    this.renderPeople();
  }

  addPerson() {
    // families add the grown-ups first, then the kids
    const adults = this.people.filter((p) => p.who === 'adult').length;
    const kids = this.people.filter((p) => p.who === 'kid').length;
    this.people.push({ who: this.current.kids && (adults >= 2 || kids > 0) ? 'kid' : 'adult', id: null });
    this.renderPeople();
    this.el.after.hidden = true;
    this.el.people.lastElementChild?.querySelector('.lsb-qs__chips button:not(:disabled)')?.focus();
  }

  removePerson(i) {
    if (this.people.length < 2) return;
    this.people.splice(i, 1);
    this.renderPeople();
    this.el.addPerson.focus();
  }

  setWho(i, who) {
    const p = this.people[i];
    if (!p || p.who === who || !['adult', 'kid', 'dog'].includes(who) || (who === 'kid' && !this.current.kids) || (who === 'dog' && !this.current.dog)) return;
    p.who = who;
    p.id = null; // the other garment has its own sizes
    this.renderPeople();
    const photo = this.photos.findIndex((image) => who === 'dog' ? image.dog : who === 'kid' ? image.kids : !image.dog && !image.kids && !image.paper);
    if (photo >= 0) this.show(photo);
    this.el.people.children[i]?.querySelector(`[data-who="${who}"]`)?.focus();
  }

  pick(i, id) {
    const p = this.people[i];
    if (!p) return;
    p.id = id ? Number(id) : null;
    this.el.people.children[i]?.querySelectorAll('.lsb-qs__chips button').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.id) === p.id)));
    this.total();
  }

  renderPeople() {
    const many = this.people.length > 1;
    const kids = !!this.current.kids;
    const dog = !!this.current.dog;
    this.el.people.classList.toggle('is-list', many || kids || dog);
    this.el.people.replaceChildren(...this.people.map((p, i) => {
      const li = document.createElement('li');
      li.className = 'lsb-person';
      li.dataset.person = String(i);
      const who = p.who === 'dog' ? 'Dog' : p.who === 'kid' ? 'Kid' : 'Grown-up';
      if (many || kids || dog) {
        const head = document.createElement('div');
        head.className = 'lsb-person__head';
        const n = document.createElement('span');
        n.className = 'lsb-person__n';
        n.textContent = `Family member ${i + 1}`;
        head.append(n);
        if (kids || dog) {
          const seg = document.createElement('div');
          seg.className = 'lsb-person__who';
          seg.setAttribute('role', 'group');
          seg.setAttribute('aria-label', `Family member ${i + 1} is`);
          for (const [value, text] of [['adult', 'Grown-up'], ...(kids ? [['kid', 'Kid']] : []), ...(dog ? [['dog', 'Dog']] : [])]) {
            const b = document.createElement('button');
            b.type = 'button';
            b.dataset.who = value;
            b.textContent = text;
            b.setAttribute('aria-pressed', String(p.who === value));
            seg.append(b);
          }
          head.append(seg);
        }
        if (many) {
          const x = document.createElement('button');
          x.type = 'button';
          x.className = 'lsb-person__x';
          x.dataset.remove = '';
          x.setAttribute('aria-label', `Remove family member ${i + 1}`);
          x.textContent = '×';
          head.append(x);
        }
        li.append(head);
      }
      const chips = document.createElement('div');
      chips.className = 'lsb-qs__chips';
      chips.setAttribute('role', 'group');
      chips.setAttribute('aria-label', many || kids || dog ? `Family member ${i + 1}, ${who.toLowerCase()} size` : 'Pajama size');
      chips.append(...this.variantsFor(p).map((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.dataset.id = v.id;
        b.textContent = v.title;
        b.setAttribute('aria-pressed', String(v.id === p.id));
        b.disabled = !v.available;
        if (!v.available) b.setAttribute('aria-label', `${v.title}, sold out`);
        return b;
      }));
      li.append(chips);
      if (p.who === 'dog') {
        const hint = document.createElement('p');
        hint.className = 'lsb-person__hint';
        hint.textContent = 'Matching bandana · square sizes S 17.3″, M 21.3″, L 25.2″. Allow room to fold and tie; measure your dog’s neck. See fabric and sizing below.';
        li.append(hint);
      }
      if (p.who === 'kid') {
        const hint = document.createElement('p');
        hint.className = 'lsb-person__hint';
        hint.textContent = `Kids' lounge pants · ${money(Math.min(...this.variantsFor(p).map((v) => v.price)))}`;
        li.append(hint);
      }
      return li;
    }));
    this.el.kidsNote.hidden = !this.people.some((p) => p.who === 'kid');
    this.total();
  }

  /** What the person's pick is, as a variant (with its garment). */
  chosen(p) {
    const v = p.id ? this.variantsFor(p).find((x) => x.id === p.id) : null;
    return v ? { ...v, kid: p.who === 'kid', dog: p.who === 'dog' } : null;
  }

  show(i) {
    const p = this.photos[i];
    if (!p) return;
    this.photoIndex = i;
    this.el.main.src = sized(p.src, 1000);
    this.el.main.alt = caption(p.alt) || this.current.name;
    this.el.thumbs.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.i) === i)));
  }

  paperVariant() {
    const v = this.dialog.querySelector('input[name="lsb-paper"]:checked')?.value;
    return v && this.current.paper ? this.current.paper.variants.find((x) => String(x.id) === v) : null;
  }

  total() {
    if (!this.current || !this.el.add) return;
    const paper = this.paperVariant();
    const missing = this.people.findIndex((p) => !this.chosen(p));
    if (missing >= 0) {
      this.el.add.textContent = this.people.length > 1 ? `Choose a size for family member ${missing + 1}` : 'Choose a size';
      this.el.add.disabled = true;
      return;
    }
    const pairs = this.people.map((p) => this.chosen(p));
    const cents = pairs.reduce((sum, v) => sum + v.price, 0) + (paper ? paper.price : 0);
    this.el.add.textContent = pairs.length > 1 ? `Add ${pairs.length} pieces to cart · ${money(cents)}` : `Add to cart · ${money(cents)}`;
    this.el.add.disabled = this.busy;
  }

  status(msg, err = false) {
    this.el.status.textContent = msg;
    this.el.status.classList.toggle('is-error', !!err);
  }

  async addToCart() {
    const pairs = this.people.map((p) => this.chosen(p));
    if (!pairs.length || pairs.some((v) => !v) || this.busy) return;
    const paper = this.paperVariant();
    // one line per size: two grown-ups in M are one line of 2
    const qty = new Map();
    for (const v of pairs) qty.set(v.id, (qty.get(v.id) || 0) + 1);
    const items = [...qty].map(([id, quantity]) => ({ id, quantity }));
    if (paper) items.push({ id: paper.id, quantity: 1 });
    this.busy = true;
    this.el.add.disabled = true;
    this.status('Adding…');
    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((n) => n.getAttribute('data-section-id')).join(',');
    const routes = window.Theme?.routes || {};
    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const parsed = await response.json().catch(() => ({}));
      if (!response.ok || parsed.status) {
        document.dispatchEvent(new CartErrorEvent('lig-swatch-book', parsed.message, parsed.description, parsed.errors));
        this.status(parsed.description || parsed.message || 'Could not add to the cart.', true);
        return;
      }
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
      document.dispatchEvent(new CartAddEvent(cart ?? parsed, 'lig-swatch-book', { source: 'lig-swatch-book', itemCount: cart?.item_count, sections: parsed.sections }));
      const sizes = pairs.map((v) => (v.dog ? `dog bandana ${v.title}` : v.kid ? `kids' ${v.title}` : v.title)).join(', ');
      this.status(`Added: ${this.current.name}, ${sizes}${paper ? `, and the ${paper.title}` : ''}.`);
      this.el.after.hidden = false;
    } catch {
      document.dispatchEvent(new CartErrorEvent('lig-swatch-book', 'Could not add to cart', undefined, undefined));
      this.status('Could not add to the cart. Try again.', true);
    } finally {
      this.busy = false;
      this.total();
    }
  }

  // ---- photo viewer -----------------------------------------------------------
  bindZoom() {
    if (!this.zoom || typeof this.zoom.showModal !== 'function') return;
    this.z = { img: this.zoom.querySelector('.lsb-zoom__img'), cap: this.zoom.querySelector('.lsb-zoom__cap'),
      prev: this.zoom.querySelector('[data-zprev]'), next: this.zoom.querySelector('[data-znext]') };
    this.zoom.addEventListener('click', (e) => {
      if (e.target.closest('[data-zclose]') || e.target === this.zoom || e.target.classList.contains('lsb-zoom__fig')) { this.zoom.close(); return; }
      if (e.target.closest('[data-zprev]')) this.step(-1);
      if (e.target.closest('[data-znext]')) this.step(1);
    });
    this.zoom.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
    });
    let x0 = null;
    this.zoom.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    this.zoom.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
    });
  }

  openZoom(set, i) {
    if (!this.z || !set?.length) return;
    this.zset = set;
    this.zi = i;
    this.renderZoom();
    if (!this.zoom.open) this.zoom.showModal();
  }

  step(dir) {
    if (!this.zset) return;
    this.zi = (this.zi + dir + this.zset.length) % this.zset.length;
    this.renderZoom();
  }

  renderZoom() {
    const p = this.zset[this.zi];
    this.z.img.src = sized(p.src, 2000);
    this.z.img.alt = caption(p.alt) || this.current?.name || '';
    this.z.cap.textContent = `${caption(p.alt) || ''}${this.zset.length > 1 ? `  ·  ${this.zi + 1} / ${this.zset.length}` : ''}`.trim();
    const many = this.zset.length > 1;
    this.z.prev.hidden = !many;
    this.z.next.hidden = !many;
  }
}

if (!customElements.get('lig-swatch-book')) {
  customElements.define('lig-swatch-book', class extends HTMLElement {
    connectedCallback() {
      if (this._lsb) return;
      this._lsb = new SwatchBook(this);
    }
  });
}
